import { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Loader2, Volume2, RotateCcw, Save, Play } from "lucide-react";

// Phase 7-D · Levi Preferences · editable settings page.
// The Meet Levi first-run flow writes to the same endpoints; this
// page is the ongoing-edit surface. Provider voice names (Onyx, Ash,
// OpenAI, TTS models) are never exposed here. Contractor sees the
// product-stable enums only.

const L = {
  en: {
    title: "Meet Levi · your preferences",
    lede: "Personalize how Levi works with you. This never changes Levi's decisions — just how they sound and how you like to hear things.",
    name_label: "What should Levi call you?",
    name_placeholder: "e.g., Sam",
    name_help: "Levi uses this on greetings only. Your full name still appears on every document.",
    voice_label: "How would you like Levi to sound?",
    voice_professional: "Professional",
    voice_professional_desc: "Grounded, steady, project-manager delivery.",
    voice_warm: "Warm",
    voice_warm_desc: "Softer cadence — like a trusted coworker.",
    style_label: "Communication style",
    style_brief: "Brief and direct",
    style_brief_desc: "Get to the point. Minimal preamble.",
    style_balanced: "Balanced",
    style_balanced_desc: "Levi's default — clear with just enough context.",
    style_conversational: "Conversational",
    style_conversational_desc: "Explains reasoning. Feels like a truck-cab chat.",
    speed_label: "Speaking speed",
    speed_slow: "Slow",
    speed_normal: "Normal",
    speed_fast: "Fast",
    read_aloud_label: "Read responses aloud",
    read_aloud_help: "You can flip this any time from here.",
    preview_button: "Preview Levi's voice",
    preview_loading: "Loading preview…",
    save: "Save",
    saved: "Saved",
    reset: "Reset to defaults",
    reset_confirm: "Reset every Levi preference to defaults? This does not delete your account or any data.",
    loading: "Loading your preferences…",
    error: "Something went wrong. Try again.",
    preview_unavailable: "Preview unavailable — try again in a moment.",
  },
  es: {
    title: "Conoce a Levi · tus preferencias",
    lede: "Personaliza cómo Levi trabaja contigo. Esto nunca cambia las decisiones de Levi — solo cómo suena y cómo prefieres escucharlo.",
    name_label: "¿Cómo debería llamarte Levi?",
    name_placeholder: "ej., Sam",
    name_help: "Levi usa esto solo en saludos. Tu nombre completo sigue apareciendo en cada documento.",
    voice_label: "¿Cómo quieres que suene Levi?",
    voice_professional: "Profesional",
    voice_professional_desc: "Firme, estable, tono de jefe de proyecto.",
    voice_warm: "Cálido",
    voice_warm_desc: "Cadencia más suave — como un compañero de confianza.",
    style_label: "Estilo de comunicación",
    style_brief: "Breve y directo",
    style_brief_desc: "Al grano. Sin rodeos.",
    style_balanced: "Equilibrado",
    style_balanced_desc: "El predeterminado de Levi — claro con el contexto justo.",
    style_conversational: "Conversacional",
    style_conversational_desc: "Explica el porqué. Como una plática en la troca.",
    speed_label: "Velocidad al hablar",
    speed_slow: "Lento",
    speed_normal: "Normal",
    speed_fast: "Rápido",
    read_aloud_label: "Leer respuestas en voz alta",
    read_aloud_help: "Puedes cambiarlo aquí cuando quieras.",
    preview_button: "Escuchar la voz de Levi",
    preview_loading: "Cargando muestra…",
    save: "Guardar",
    saved: "Guardado",
    reset: "Restablecer",
    reset_confirm: "¿Restablecer todas las preferencias de Levi a los valores predeterminados? Esto no elimina tu cuenta ni datos.",
    loading: "Cargando tus preferencias…",
    error: "Algo salió mal. Inténtalo de nuevo.",
    preview_unavailable: "Muestra no disponible — inténtalo en un momento.",
  },
};

const OptionTile = ({ selected, onClick, title, description, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`text-left rounded-xl border-2 px-4 py-3 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500
      ${selected
        ? "border-orange-500 bg-orange-50"
        : "border-slate-200 bg-white hover:border-slate-300"}`}
  >
    <div className="flex items-center gap-2">
      <span className={`inline-block h-3 w-3 rounded-full border-2 ${selected ? "border-orange-500 bg-orange-500" : "border-slate-300"}`} />
      <span className="font-semibold text-slate-900">{title}</span>
    </div>
    {description && (
      <p className="text-sm text-slate-600 mt-1 pl-5">{description}</p>
    )}
  </button>
);

const SettingsLevi = () => {
  const { lang } = useLang();
  const { user, refreshMe } = useAuth();
  const t = L[lang === "es" ? "es" : "en"];
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);
  const previewCacheRef = useRef({}); // { key -> objectURL }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/user/levi-preferences");
        if (!cancelled) setPrefs(r.data);
      } catch (e) {
        if (!cancelled) {
          toast.error(t.error);
          setPrefs({
            levi_preferred_name: null,
            levi_voice_id: "professional",
            levi_communication_style: "balanced",
            levi_speaking_speed: "normal",
            levi_read_aloud_enabled: null,
            preferred_name_effective: (user?.full_name || "").split(" ")[0] || "",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readAloudEffective = useMemo(() => {
    if (!prefs) return true;
    if (prefs.levi_read_aloud_enabled === null
        || prefs.levi_read_aloud_enabled === undefined) {
      try {
        return localStorage.getItem("jc:levi:tts-enabled") !== "0";
      } catch { return true; }
    }
    return !!prefs.levi_read_aloud_enabled;
  }, [prefs]);

  const setField = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

  const save = async (partial) => {
    setSaving(true);
    try {
      const body = partial || {
        levi_preferred_name: prefs.levi_preferred_name || null,
        levi_voice_id: prefs.levi_voice_id,
        levi_communication_style: prefs.levi_communication_style,
        levi_speaking_speed: prefs.levi_speaking_speed,
        levi_read_aloud_enabled:
          prefs.levi_read_aloud_enabled === null
            ? undefined
            : prefs.levi_read_aloud_enabled,
      };
      Object.keys(body).forEach(
        (k) => body[k] === undefined && delete body[k]);
      const r = await api.put("/user/levi-preferences", body);
      setPrefs(r.data);
      // Mirror read-aloud into localStorage for the fast client cache.
      if (r.data.levi_read_aloud_enabled !== null
          && r.data.levi_read_aloud_enabled !== undefined) {
        try {
          localStorage.setItem(
            "jc:levi:tts-enabled",
            r.data.levi_read_aloud_enabled ? "1" : "0",
          );
        } catch { /* noop */ }
      }
      toast.success(t.saved);
      try { refreshMe && refreshMe(); } catch { /* noop */ }
    } catch (e) {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm(t.reset_confirm)) return;
    setSaving(true);
    try {
      const r = await api.post("/user/levi-preferences/reset");
      setPrefs(r.data);
      toast.success(t.saved);
      try { refreshMe && refreshMe(); } catch { /* noop */ }
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const preview = async (voiceId) => {
    setPreviewing(true);
    try {
      const key = `${voiceId}:${lang}:greeting`;
      let url = previewCacheRef.current[key];
      if (!url) {
        const token = localStorage.getItem("jc_token") || "";
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${apiBase}/api/levi/tts/preview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            voice_id: voiceId,
            language: lang === "es" ? "es" : "en",
            sample_text_key: "greeting",
          }),
        });
        if (!r.ok) throw new Error(`http_${r.status}`);
        const blob = await r.blob();
        url = URL.createObjectURL(blob);
        previewCacheRef.current[key] = url;
      }
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      } catch { /* noop */ }
      const audio = new Audio(url);
      audioRef.current = audio;
      await audio.play();
    } catch (e) {
      toast.error(t.preview_unavailable);
    } finally {
      setPreviewing(false);
    }
  };

  if (!prefs) {
    return (
      <DashboardLayout>
        <div className="flex items-center gap-2 text-slate-600 p-8">
          <Loader2 className="animate-spin h-4 w-4" />
          <span>{t.loading}</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-6 space-y-8" data-testid="settings-levi-root">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t.title}</h1>
          <p className="text-sm text-slate-600 mt-1">{t.lede}</p>
        </div>

        {/* Preferred name */}
        <section className="space-y-2">
          <label className="block text-sm font-semibold text-slate-800">
            {t.name_label}
          </label>
          <input
            data-testid="settings-levi-name-input"
            type="text"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            value={prefs.levi_preferred_name || ""}
            placeholder={t.name_placeholder}
            maxLength={60}
            onChange={(e) => setField("levi_preferred_name", e.target.value)}
          />
          <p className="text-xs text-slate-500">{t.name_help}</p>
        </section>

        {/* Voice */}
        <section className="space-y-2">
          <label className="block text-sm font-semibold text-slate-800">
            {t.voice_label}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <OptionTile
              selected={prefs.levi_voice_id === "professional"}
              onClick={() => setField("levi_voice_id", "professional")}
              title={t.voice_professional}
              description={t.voice_professional_desc}
              testid="settings-levi-voice-professional"
            />
            <OptionTile
              selected={prefs.levi_voice_id === "warm"}
              onClick={() => setField("levi_voice_id", "warm")}
              title={t.voice_warm}
              description={t.voice_warm_desc}
              testid="settings-levi-voice-warm"
            />
          </div>
          <div className="pt-1">
            <button
              type="button"
              data-testid="settings-levi-preview-btn"
              onClick={() => preview(prefs.levi_voice_id)}
              disabled={previewing}
              className="inline-flex items-center gap-2 text-sm border border-slate-300 rounded-full px-3 py-1 hover:bg-slate-50 disabled:opacity-60"
            >
              {previewing
                ? <><Loader2 className="animate-spin h-3.5 w-3.5" /> {t.preview_loading}</>
                : <><Play className="h-3.5 w-3.5" /> {t.preview_button}</>}
            </button>
          </div>
        </section>

        {/* Style */}
        <section className="space-y-2">
          <label className="block text-sm font-semibold text-slate-800">
            {t.style_label}
          </label>
          <div className="grid grid-cols-1 gap-2">
            <OptionTile
              selected={prefs.levi_communication_style === "brief"}
              onClick={() => setField("levi_communication_style", "brief")}
              title={t.style_brief}
              description={t.style_brief_desc}
              testid="settings-levi-style-brief"
            />
            <OptionTile
              selected={prefs.levi_communication_style === "balanced"}
              onClick={() => setField("levi_communication_style", "balanced")}
              title={t.style_balanced}
              description={t.style_balanced_desc}
              testid="settings-levi-style-balanced"
            />
            <OptionTile
              selected={prefs.levi_communication_style === "conversational"}
              onClick={() => setField("levi_communication_style", "conversational")}
              title={t.style_conversational}
              description={t.style_conversational_desc}
              testid="settings-levi-style-conversational"
            />
          </div>
        </section>

        {/* Speed */}
        <section className="space-y-2">
          <label className="block text-sm font-semibold text-slate-800">
            {t.speed_label}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {["slow", "normal", "fast"].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`settings-levi-speed-${s}`}
                onClick={() => setField("levi_speaking_speed", s)}
                className={`rounded-lg border-2 px-3 py-2 text-sm font-medium
                  ${prefs.levi_speaking_speed === s
                    ? "border-orange-500 bg-orange-50 text-orange-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
              >
                {t[`speed_${s}`]}
              </button>
            ))}
          </div>
        </section>

        {/* Read aloud */}
        <section className="space-y-2">
          <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                <Volume2 className="h-4 w-4" /> {t.read_aloud_label}
              </div>
              <div className="text-xs text-slate-500">{t.read_aloud_help}</div>
            </div>
            <input
              data-testid="settings-levi-read-aloud"
              type="checkbox"
              className="h-5 w-5 accent-orange-500"
              checked={readAloudEffective}
              onChange={(e) => setField("levi_read_aloud_enabled", e.target.checked)}
            />
          </label>
        </section>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            data-testid="settings-levi-reset-btn"
            onClick={resetAll}
            disabled={saving}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <RotateCcw className="h-4 w-4" /> {t.reset}
          </button>
          <button
            type="button"
            data-testid="settings-levi-save-btn"
            onClick={() => save()}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-60"
          >
            {saving
              ? <><Loader2 className="animate-spin h-4 w-4" /> {t.save}</>
              : <><Save className="h-4 w-4" /> {t.save}</>}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsLevi;
